/**
 * Permission directive for Vue components
 * Usage: v-permission="'admin.users.index'" or v-permission="['admin.users.index', 'admin.users.store']"
 */

import { hasPermission, hasAnyPermission, hasModule, hasAction } from '@/utils/permissions'
import store from '@/store'

// Original permission directive (works with full permission strings)
const permissionDirective = {
  mounted(el, binding) {
    const { value, modifiers } = binding
    
    // Don't check permissions during logout to prevent DOM errors
    if (store.state.isLoggingOut) {
      el.style.display = 'none'
      return
    }
    
    let hasAccess = false
    
    if (typeof value === 'string') {
      hasAccess = hasPermission(value)
    } else if (Array.isArray(value)) {
      // Use 'all' modifier to require all permissions, otherwise any permission is enough
      hasAccess = modifiers.all 
        ? value.every(permission => hasPermission(permission))
        : hasAnyPermission(value)
    }
    
    if (!hasAccess) {
      // Remove the element from DOM if no permission
      el.remove()
    }
  },
  
  updated(el, binding) {
    // Handle dynamic permission changes
    const { value, modifiers } = binding
    
    // Don't check permissions during logout to prevent DOM errors
    if (store.state.isLoggingOut) {
      el.style.display = 'none'
      return
    }
    
    let hasAccess = false
    
    if (typeof value === 'string') {
      hasAccess = hasPermission(value)
    } else if (Array.isArray(value)) {
      hasAccess = modifiers.all 
        ? value.every(permission => hasPermission(permission))
        : hasAnyPermission(value)
    }
    
    if (hasAccess) {
      el.style.display = ''
    } else {
      el.style.display = 'none'
    }
  }
}

// Module-based directive (v-module="'users'")
const moduleDirective = {
  mounted(el, binding) {
    const { value } = binding
    
    if (store.state.isLoggingOut) {
      el.style.display = 'none'
      return
    }
    
    if (!hasModule(value)) {
      el.remove()
    }
  },
  
  updated(el, binding) {
    const { value } = binding
    
    if (store.state.isLoggingOut) {
      el.style.display = 'none'
      return
    }
    
    if (hasModule(value)) {
      el.style.display = ''
    } else {
      el.style.display = 'none'
    }
  }
}

// Action-based directive (v-action="{ module: 'users', action: 'store' }")
const actionDirective = {
  mounted(el, binding) {
    const { value } = binding
    
    if (store.state.isLoggingOut) {
      el.style.display = 'none'
      return
    }
    
    if (typeof value === 'object' && value.module && value.action) {
      if (!hasAction(value.module, value.action)) {
        el.remove()
      }
    } else {
      el.remove()
    }
  },
  
  updated(el, binding) {
    const { value } = binding
    
    if (store.state.isLoggingOut) {
      el.style.display = 'none'
      return
    }
    
    if (typeof value === 'object' && value.module && value.action) {
      if (hasAction(value.module, value.action)) {
        el.style.display = ''
      } else {
        el.style.display = 'none'
      }
    } else {
      el.style.display = 'none'
    }
  }
}

export default permissionDirective
export { moduleDirective, actionDirective } 